/**
 * Main App component with routing and theme provider
 */

import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SnackbarProvider } from 'notistack';
import { themes } from './core/theme';
import { useThemeStore, useAuthStore } from './core/store';
import { ROUTES } from './core/constants';
import { initAmplitude } from './core/amplitude/amplitude';

// Layouts
import AppLayout from './shared/layouts/AppLayout';

// Pages
import LoginPage from './features/auth/LoginPage';

// Audit Page
import AuditPage from './features/audit/AuditPage';
import EmbeddedAutoLogin from 'features/autologin/embedded-autologin';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { mode } = useThemeStore();
  const theme = themes[mode];

  useEffect(() => {
    initAmplitude();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SnackbarProvider maxSnack={3}>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />

              {/* Protected Routes */}

              {/* Audit Route */}
              <Route
                path={ROUTES.AUDIT}
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <AuditPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/embedded-autologin"
                element={<EmbeddedAutoLogin />}
              />
              {/* Default redirect */}
              <Route
                path="/"
                element={<Navigate to={ROUTES.AUDIT} replace />}
              />

              {/* Catch all route */}
              <Route
                path="*"
                element={<Navigate to={ROUTES.AUDIT} replace />}
              />
            </Routes>
          </Router>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
